import { Formik } from 'formik'
import * as Yup from 'yup'
import { XStack, YStack } from 'tamagui'
import { useToastController } from '@tamagui/toast'
import { Input } from '@components/Input'
import { Button } from '@components/Button'
import { SectionCard } from '@components/Profile/SectionCard'
import { useComponentTranslation } from '@hooks/useComponentTranslation'
import { useUserUpdate } from '@services/api/graphql/mutations/userUpdate'
import { IUserProfile, IUserProfileInput } from '@dtos/User'
import { isValidCpf, isValidRg } from '@utils/cpf'

interface IPersonalDataSectionProps {
  profile: IUserProfile
}

interface IPersonalDataForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  cpf: string
  rg: string
  bornAt: string
  street: string
  number: string
  complement: string
  district: string
  city: string
  state: string
  zipCode: string
}

export function PersonalDataSection({ profile }: IPersonalDataSectionProps) {
  const { t } = useComponentTranslation('Profile')
  const toast = useToastController()
  const { handleUpdateUser, loading } = useUserUpdate()

  const validationSchema = Yup.object({
    firstName: Yup.string().required(t('errors.required')),
    lastName: Yup.string().required(t('errors.required')),
    email: Yup.string()
      .email(t('errors.invalidEmail'))
      .required(t('errors.required')),
    cpf: Yup.string()
      .required(t('errors.required'))
      .test('valid-cpf', t('errors.invalidCpf'), (value) => isValidCpf(value)),
    rg: Yup.string().test(
      'valid-rg',
      t('errors.invalidRg'),
      (value) => !value || isValidRg(value),
    ),
  })

  const initialValues: IPersonalDataForm = {
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    email: profile.email ?? '',
    phone: profile.phone ?? '',
    cpf: profile.cpf ?? '',
    rg: profile.rg ?? '',
    bornAt: profile.bornAt ?? '',
    street: profile.address?.street ?? '',
    number: profile.address?.number ?? '',
    complement: profile.address?.complement ?? '',
    district: profile.address?.district ?? '',
    city: profile.address?.city ?? '',
    state: profile.address?.state ?? '',
    zipCode: profile.address?.zipCode ?? '',
  }

  async function handleSave(values: IPersonalDataForm) {
    const attributes: IUserProfileInput = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      cpf: values.cpf,
      rg: values.rg,
      bornAt: values.bornAt || undefined,
      address: {
        id: profile.address?.id,
        street: values.street,
        number: values.number,
        complement: values.complement,
        district: values.district,
        city: values.city,
        state: values.state,
        zipCode: values.zipCode,
      },
    }

    const result = await handleUpdateUser(attributes)
    if (result?.errors?.length) {
      toast.show(t('toast.errorTitle'), {
        message: result.errors.join('\n'),
        type: 'error',
      })
      return
    }

    toast.show(t('toast.successTitle'), { message: t('toast.successMessage') })
  }

  return (
    <SectionCard title={t('personalData.title')}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSave}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <YStack gap="$size.spacing-xs">
            <XStack gap="$size.spacing-xs" $sm={{ flexDirection: 'column' }}>
              <YStack flex={1}>
                <Input
                  label={t('personalData.firstName')}
                  value={values.firstName}
                  onChangeText={handleChange('firstName')}
                  onBlur={handleBlur('firstName')}
                  errorMessage={touched.firstName && errors.firstName}
                />
              </YStack>
              <YStack flex={1}>
                <Input
                  label={t('personalData.lastName')}
                  value={values.lastName}
                  onChangeText={handleChange('lastName')}
                  onBlur={handleBlur('lastName')}
                  errorMessage={touched.lastName && errors.lastName}
                />
              </YStack>
            </XStack>

            <XStack gap="$size.spacing-xs" $sm={{ flexDirection: 'column' }}>
              <YStack flex={1}>
                <Input
                  label={t('personalData.email')}
                  keyboardType="email-address"
                  value={values.email}
                  onChangeText={handleChange('email')}
                  onBlur={handleBlur('email')}
                  errorMessage={touched.email && errors.email}
                />
              </YStack>
              <YStack flex={1}>
                <Input
                  label={t('personalData.phone')}
                  keyboardType="phone-pad"
                  mask="BRL_PHONE"
                  value={values.phone}
                  onChangeText={handleChange('phone')}
                />
              </YStack>
            </XStack>

            <XStack gap="$size.spacing-xs" $sm={{ flexDirection: 'column' }}>
              <YStack flex={1}>
                <Input
                  label={t('personalData.cpf')}
                  mask="CPF_ALPHANUMERIC"
                  autoCapitalize="characters"
                  value={values.cpf}
                  onChangeText={(text) =>
                    setFieldValue('cpf', text.toUpperCase())
                  }
                  onBlur={handleBlur('cpf')}
                  errorMessage={touched.cpf && errors.cpf}
                />
              </YStack>
              <YStack flex={1}>
                <Input
                  label={t('personalData.rg')}
                  mask="BRL_RG"
                  keyboardType="numeric"
                  value={values.rg}
                  onChangeText={handleChange('rg')}
                  onBlur={handleBlur('rg')}
                  errorMessage={touched.rg && errors.rg}
                />
              </YStack>
            </XStack>

            <XStack gap="$size.spacing-xs" $sm={{ flexDirection: 'column' }}>
              <YStack flex={2}>
                <Input
                  label={t('address.street')}
                  value={values.street}
                  onChangeText={handleChange('street')}
                />
              </YStack>
              <YStack flex={1}>
                <Input
                  label={t('address.number')}
                  keyboardType="numeric"
                  value={values.number}
                  onChangeText={handleChange('number')}
                />
              </YStack>
            </XStack>

            <XStack gap="$size.spacing-xs" $sm={{ flexDirection: 'column' }}>
              <YStack flex={1}>
                <Input
                  label={t('address.complement')}
                  value={values.complement}
                  onChangeText={handleChange('complement')}
                />
              </YStack>
              <YStack flex={1}>
                <Input
                  label={t('address.district')}
                  value={values.district}
                  onChangeText={handleChange('district')}
                />
              </YStack>
            </XStack>

            <XStack gap="$size.spacing-xs" $sm={{ flexDirection: 'column' }}>
              <YStack flex={2}>
                <Input
                  label={t('address.city')}
                  value={values.city}
                  onChangeText={handleChange('city')}
                />
              </YStack>
              <YStack flex={1}>
                <Input
                  label={t('address.state')}
                  value={values.state}
                  onChangeText={handleChange('state')}
                />
              </YStack>
              <YStack flex={1}>
                <Input
                  label={t('address.zipCode')}
                  keyboardType="numeric"
                  mask="ZIP_CODE"
                  value={values.zipCode}
                  onChangeText={handleChange('zipCode')}
                />
              </YStack>
            </XStack>

            <XStack justifyContent="flex-end">
              <Button
                variant="primary"
                loading={loading}
                onPress={() => handleSubmit()}
              >
                {t('actions.save')}
              </Button>
            </XStack>
          </YStack>
        )}
      </Formik>
    </SectionCard>
  )
}
